import React from 'react';
import { Header } from './Header';
import { ProblemPanel } from './ProblemPanel';
import { Editor } from './Editor';
import { EditorToolbar } from './EditorToolbar';
import { ResultsPanel } from './ResultsPanel';
import { useResizableSidebar } from '../hooks/useResizableSidebar';
import { useTheme } from '@librerank/ui';
import type { Problem } from '../types/problem';
import type { TestResult } from '../types/worker';
import type { LanguageId } from '../types/language';

interface RunnerLayoutProps {
  problem: Problem | null;
  code: string;
  onCodeChange: (code: string) => void;
  language: LanguageId;
  onRun: () => void;
  resultsMode: 'idle' | 'code' | 'tests';
  testResults: TestResult[];
  stdout: string;
  stderr: string;
  error: string;
  headerContent?: React.ReactNode;
  controls: React.ReactNode;
  isMobile?: boolean;
}

export const RunnerLayout: React.FC<RunnerLayoutProps> = ({
  problem,
  code,
  onCodeChange,
  language,
  onRun,
  resultsMode,
  testResults,
  stdout,
  stderr,
  error,
  headerContent,
  controls,
  isMobile = false
}) => {
  const { theme } = useTheme();
  const { 
    width: sidebarWidth, 
    isCollapsed, 
    startResizing, 
    toggleSidebar 
  } = useResizableSidebar();

  const [mobileTab, setMobileTab] = React.useState<'problem' | 'editor'>('problem');

  return (
    <div className="app-container">
      <Header workspaceId={problem?.workspaceId}>
        {headerContent}
        {!isMobile && (
          <div style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
            {controls}
          </div>
        )}
      </Header>
      
      {isMobile && (
        <div className="mobile-tabs">
          <button 
            className={`mobile-tab-btn ${mobileTab === 'problem' ? 'active' : ''}`}
            onClick={() => setMobileTab('problem')}
          >
            Problem
          </button>
          <button 
            className={`mobile-tab-btn ${mobileTab === 'editor' ? 'active' : ''}`}
            onClick={() => setMobileTab('editor')}
          >
            Code & Results
          </button>
        </div>
      )}

      <div className="main-content">
        {(!isMobile || mobileTab === 'problem') && (
          !isMobile && isCollapsed ? (
            <div className="collapsed-sidebar" onClick={toggleSidebar}>
              <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Problem</span>
              <button className="toggle-btn">»</button>
            </div>
          ) : (
            <div style={{ 
              width: isMobile ? '100%' : sidebarWidth, 
              display: 'flex', 
              flexDirection: 'column',
              borderRight: isMobile ? 'none' : undefined
            }}>
              <ProblemPanel
                problem={problem}
                onToggle={isMobile ? undefined : toggleSidebar}
              />
            </div>
          )
        )}
        
        {!isMobile && !isCollapsed && (
          <div className="resize-handle" onMouseDown={startResizing} />
        )}

        {(!isMobile || mobileTab === 'editor') && (
          <div className="editor-container">
            <EditorToolbar>
              {isMobile && (
                <div style={{ marginTop: '0.5rem' }}>
                  {controls}
                </div>
              )}
            </EditorToolbar>
            <Editor
              code={code}
              onChange={onCodeChange}
              onRun={onRun}
              theme={theme}
              language={language}
            />
            <ResultsPanel
              mode={resultsMode}
              results={testResults}
              stdout={stdout}
              stderr={stderr}
              error={error}
            />
          </div>
        )}
      </div>
    </div>
  );
};

